// src/pages/UsersPage.jsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Card, Avatar, TextField, InputAdornment,
  IconButton, CircularProgress, Tooltip, alpha, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogContent, DialogTitle, DialogActions, Tabs, Tab,
  Pagination, Select, MenuItem, FormControl, InputLabel, Snackbar, Alert,
  Skeleton, Divider, Switch, FormControlLabel,
} from '@mui/material';
import {
  Search, Refresh, Person, CheckCircle, Cancel, HowToReg,
  Close, Phone, LocationOn, Work, Badge as BadgeIcon,
  CalendarToday, Fingerprint, WarningAmber, FiberManualRecord,
  Edit, Add, PersonAdd,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { selectToken } from '../store/authSlice';

const API_BASE    = 'https://monitoring-api.govirtual.cloud/api/v1/users';
const LOOKUP_BASE = 'https://monitoring-api.govirtual.cloud/api/v1';

// ── Utilities ─────────────────────────────────────────────────────────────────
function fullName(u) {
  return [u?.firstName, u?.lastName].filter(Boolean).join(' ') || '—';
}
function initials(u) {
  return [(u?.firstName || '')[0], (u?.lastName || '')[0]].filter(Boolean).join('').toUpperCase() || '?';
}
function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return '—'; }
}
const AVATAR_COLORS = ['#91ce0f','#0a9e74','#7b1fa2','#c06000','#d32f2f','#0288d1','#558b2f','#6d4c41'];
function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Hook: fetch all dropdown lists in parallel ────────────────────────────────
function useDropdowns(token, enabled = true) {
  const [lists, setLists] = useState({ roles: [], regions: [], projects: [], salesOffices: [], territories: [], teams: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !token) return;
    setLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    const get = (path) =>
      fetch(`${LOOKUP_BASE}/${path}`, { headers: h })
        .then((r) => r.json())
        .then((d) => Array.isArray(d) ? d : (d.rows || d.data || d.results || []))
        .catch(() => []);

    Promise.all([
      get('role'), get('region'), get('project'),
      get('salesoffice'), get('territory'), get('team'),
    ]).then(([roles, regions, projects, salesOffices, territories, teams]) => {
      setLists({ roles, regions, projects, salesOffices, territories, teams });
      setLoading(false);
    });
  }, [token, enabled]);

  return { lists, loading };
}

// ── Dropdown select helper ────────────────────────────────────────────────────
function LookupSelect({ label, value, onChange, items, loading, idKey = 'id', labelKey = 'title' }) {
  if (loading) {
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="rounded" height={14} width="45%" sx={{ mb: 0.75, borderRadius: '4px' }} />
        <Skeleton variant="rounded" height={40} sx={{ borderRadius: '10px' }} />
      </Box>
    );
  }
  // Only pass value once items are loaded — prevents MUI out-of-range warning
  // when the form pre-fills before the dropdown list has arrived
  const safeValue = items.length > 0 ? (value || '') : '';
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select value={safeValue} onChange={(e) => onChange(e.target.value)}
        label={label} sx={{ borderRadius: '10px' }}>
        <MenuItem value=""><em>None</em></MenuItem>
        {items.map((item) => (
          <MenuItem key={item[idKey]} value={item[idKey]}>
            {item[labelKey] || item.name || item.code || item[idKey]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#91ce0f', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {children}
    </Typography>
  );
}

// ── User form (shared by Add and Edit) ────────────────────────────────────────
const BLANK_FORM = {
  firstName: '', lastName: '', username: '', email: '',
  mobileNumber: '', password: '',
  role: '', project: '', region: '', salesOffice: '', territory: '', team: '',
  city: '', province: '', idNumber: '', birthday: '',
  active: true,
};

function UserFormDialog({ open, onClose, initialUser, onSaved, token }) {
  const isEdit = !!initialUser;
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const { lists, loading: ddLoading } = useDropdowns(token, open);

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialUser) {
      setForm({
        firstName:   initialUser.firstName    || '',
        lastName:    initialUser.lastName     || '',
        username:    initialUser.username     || '',
        email:       initialUser.email        || '',
        mobileNumber: initialUser.mobileNumber || '',
        password:    '',
        role:        initialUser.role?.id    || initialUser.role?._id    || (typeof initialUser.role    === 'string' ? initialUser.role    : '') ,
        project:     initialUser.project?.id  || initialUser.project?._id  || (typeof initialUser.project  === 'string' ? initialUser.project  : ''),
        region:      initialUser.region?.id   || initialUser.region?._id   || (typeof initialUser.region   === 'string' ? initialUser.region   : ''),
        salesOffice: initialUser.salesOffice?.id || initialUser.salesOffice?._id || (typeof initialUser.salesOffice === 'string' ? initialUser.salesOffice : ''),
        territory:   initialUser.territory?.id   || initialUser.territory?._id   || (typeof initialUser.territory   === 'string' ? initialUser.territory   : ''),
        team:        initialUser.team?.id         || initialUser.team?._id         || (typeof initialUser.team         === 'string' ? initialUser.team         : ''),
        city:        initialUser.city      || '',
        province:    initialUser.province  || '',
        idNumber:    initialUser.idNumber  || '',
        birthday:    initialUser.birthday ? initialUser.birthday.split('T')[0] : '',
        active:      initialUser.active !== false,
      });
    } else {
      setForm(BLANK_FORM);
    }
    setErrors({});
  }, [open, initialUser, isEdit]);

  const set = (field) => (e) => {
    const val = e?.target !== undefined ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: val }));
    setErrors((er) => ({ ...er, [field]: '', _global: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.username.trim())  e.username  = 'Required';
    if (!isEdit && !form.password.trim()) e.password = 'Required for new users';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      ['role','project','region','salesOffice','territory','team'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      if (!payload.birthday) delete payload.birthday;

      const url    = isEdit ? `${API_BASE}/${initialUser._id}` : `${API_BASE}/create`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Request failed (${res.status})`);
      }
      const saved = await res.json();
      onSaved(saved.data || saved.user || saved, isEdit);
    } catch (err) {
      setErrors((e) => ({ ...e, _global: err.message }));
    }
    setSaving(false);
  };

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: { xs: '98vw', md: '640px' }, maxHeight: '92vh', borderRadius: '20px', overflow: 'hidden' } }}>
      <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: '#e6feff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {isEdit ? <Edit sx={{ fontSize: 18, color: '#91ce0f' }} /> : <PersonAdd sx={{ fontSize: 18, color: '#91ce0f' }} />}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f2137', flex: 1 }}>
          {isEdit ? `Edit ${fullName(initialUser)}` : 'Add New User'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9aacb8' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        {errors._global && (
          <Box sx={{ mx: 3, mt: 2, px: 2, py: 1.25, borderRadius: '10px', background: '#fff5f5', border: '1px solid #fca5a5' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#ef4444' }}>{errors._global}</Typography>
          </Box>
        )}

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ── Personal Info ── */}
          <Box>
            <SectionLabel>Personal Information</SectionLabel>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="First Name *" value={form.firstName} onChange={set('firstName')}
                  error={!!errors.firstName} helperText={errors.firstName} fullWidth sx={fieldSx} />
                <TextField size="small" label="Last Name *" value={form.lastName} onChange={set('lastName')}
                  error={!!errors.lastName} helperText={errors.lastName} fullWidth sx={fieldSx} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="City" value={form.city} onChange={set('city')} fullWidth sx={fieldSx} />
                <TextField size="small" label="Province" value={form.province} onChange={set('province')} fullWidth sx={fieldSx} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Birthday" type="date" value={form.birthday} onChange={set('birthday')}
                  fullWidth sx={fieldSx} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="ID Number" value={form.idNumber} onChange={set('idNumber')} fullWidth sx={fieldSx} />
              </Stack>
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(16,117,186,0.07)' }} />

          {/* ── Account ── */}
          <Box>
            <SectionLabel>Account</SectionLabel>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Username *" value={form.username} onChange={set('username')}
                  error={!!errors.username} helperText={errors.username} fullWidth sx={fieldSx} />
                <TextField size="small" label="Mobile Number" value={form.mobileNumber} onChange={set('mobileNumber')} fullWidth sx={fieldSx} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Email" type="email" value={form.email} onChange={set('email')} fullWidth sx={fieldSx} />
                <TextField size="small" label={isEdit ? 'New Password (leave blank to keep)' : 'Password *'} type="password"
                  value={form.password} onChange={set('password')}
                  error={!!errors.password} helperText={errors.password} fullWidth sx={fieldSx} />
              </Stack>
              {isEdit && (
                <FormControlLabel
                  control={<Switch checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#91ce0f' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#91ce0f' } }} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Active account</Typography>} />
              )}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: 'rgba(16,117,186,0.07)' }} />

          {/* ── Assignments ── */}
          <Box>
            <SectionLabel>Assignments</SectionLabel>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <LookupSelect label="Role" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))}
                  items={lists.roles} loading={ddLoading} />
                <LookupSelect label="Project" value={form.project} onChange={(v) => setForm((f) => ({ ...f, project: v }))}
                  items={lists.projects} loading={ddLoading} />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <LookupSelect label="Region" value={form.region} onChange={(v) => setForm((f) => ({ ...f, region: v }))}
                  items={lists.regions} loading={ddLoading} />
                <LookupSelect label="Sales Office" value={form.salesOffice} onChange={(v) => setForm((f) => ({ ...f, salesOffice: v }))}
                  items={lists.salesOffices} loading={ddLoading} />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <LookupSelect label="Territory" value={form.territory} onChange={(v) => setForm((f) => ({ ...f, territory: v }))}
                  items={lists.territories} loading={ddLoading} />
                <LookupSelect label="Team" value={form.team} onChange={(v) => setForm((f) => ({ ...f, team: v }))}
                  items={lists.teams} loading={ddLoading} />
              </Stack>
            </Stack>
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(16,117,186,0.08)', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}
          sx={{ borderRadius: '10px', borderColor: '#e5e7eb', color: '#4a6a85', textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : isEdit ? <Edit /> : <PersonAdd />}
          sx={{ borderRadius: '10px', background: '#91ce0f', textTransform: 'none', fontWeight: 700, boxShadow: '0 2px 8px rgba(16,117,186,0.3)', '&:hover': { background: '#0d64a3' } }}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── User detail / view modal ──────────────────────────────────────────────────
function UserModal({ user, open, onClose, onApprove, approving, onEdit }) {
  if (!user) return null;
  const color = avatarColor(fullName(user));
  const isUnapproved = user.notApproved;

  const infoRows = [
    { icon: <BadgeIcon sx={{ fontSize: 15 }} />,    label: 'Username',     value: `@${user.username}` },
    { icon: <Phone sx={{ fontSize: 15 }} />,         label: 'Mobile',       value: user.mobileNumber || '—' },
    { icon: <Work sx={{ fontSize: 15 }} />,          label: 'Role',         value: user.role?.title || '—' },
    { icon: <Work sx={{ fontSize: 15 }} />,          label: 'Project',      value: user.project?.title || '—' },
    { icon: <LocationOn sx={{ fontSize: 15 }} />,    label: 'Region',       value: user.region?.title || '—' },
    { icon: <LocationOn sx={{ fontSize: 15 }} />,    label: 'Sales Office', value: user.salesOffice?.title || user.salesOffice?.name || '—' },
    { icon: <LocationOn sx={{ fontSize: 15 }} />,    label: 'Territory',    value: user.territory?.title || user.territory?.name || '—' },
    { icon: <Work sx={{ fontSize: 15 }} />,          label: 'Team',         value: user.team?.title || user.team?.name || '—' },
    { icon: <LocationOn sx={{ fontSize: 15 }} />,    label: 'City',         value: [user.city, user.province].filter(Boolean).join(', ') || '—' },
    { icon: <CalendarToday sx={{ fontSize: 15 }} />, label: 'Birthday',     value: fmtDate(user.birthday) },
    { icon: <CalendarToday sx={{ fontSize: 15 }} />, label: 'Joined',       value: fmtDate(user.createdAt) },
    { icon: <Fingerprint sx={{ fontSize: 15 }} />,   label: 'ID Number',    value: user.idNumber || '—' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: { xs: '98vw', md: '560px' }, maxHeight: '90vh', borderRadius: '20px', background: '#fff', boxShadow: '0 24px 64px rgba(15,33,55,0.18)', overflow: 'hidden' } }}>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Hero */}
        <Box sx={{ position: 'relative', background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha('#91ce0f', 0.05)} 100%)`, borderBottom: '1px solid rgba(16,117,186,0.1)', p: 3, flexShrink: 0 }}>
          <Box sx={{ position: 'absolute', right: -30, top: -30, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(color, 0.12)}, transparent 70%)`, pointerEvents: 'none' }} />
          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12, color: '#4a6a85' }}>
            <Close fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={user.photoURL}
              sx={{ width: 76, height: 76, fontSize: '1.5rem', fontWeight: 700, background: color, border: `3px solid ${color}`, boxShadow: `0 0 0 4px ${alpha(color, 0.18)}, 0 6px 20px rgba(15,33,55,0.15)` }}>
              {initials(user)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.4 }}>{fullName(user)}</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#4a6a85', mb: 1 }}>@{user.username}</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                <Chip size="small"
                  icon={isUnapproved ? <WarningAmber sx={{ fontSize: '12px !important' }} /> : <CheckCircle sx={{ fontSize: '12px !important' }} />}
                  label={isUnapproved ? 'Pending Approval' : 'Approved'}
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, background: isUnapproved ? '#fff4e0' : '#e6f9f4', color: isUnapproved ? '#c06000' : '#0a9e74', border: `1px solid ${isUnapproved ? '#ffd599' : '#b3ead9'}` }} />
                {user.role?.title && (
                  <Chip size="small" label={user.role.title}
                    sx={{ height: 22, fontSize: '0.7rem', background: '#e6feff', color: '#91ce0f', border: '1px solid #7ec242' }} />
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {user.photoURL && (
            <Box sx={{ p: 2.5, pb: 0 }}>
              <Box component="img" src={user.photoURL} alt="profile"
                sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: '12px', border: '1px solid rgba(16,117,186,0.1)' }} />
            </Box>
          )}
          <Box sx={{ p: 2.5 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#91ce0f', mb: 1.5 }}>Profile Information</Typography>
            <Stack spacing={0}>
              {infoRows.map(({ icon, label, value }, i) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: i < infoRows.length - 1 ? '1px solid rgba(16,117,186,0.06)' : 'none' }}>
                  <Box sx={{ color: '#91ce0f', display: 'flex', flexShrink: 0 }}>{icon}</Box>
                  <Typography sx={{ fontSize: '0.78rem', color: '#4a6a85', minWidth: 95 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.83rem', color: '#0f2137', fontWeight: 500, flex: 1 }}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          {user.idURL && (
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Divider sx={{ mb: 2, borderColor: 'rgba(16,117,186,0.08)' }} />
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#91ce0f', mb: 1.5 }}>ID Document</Typography>
              <Box component="img" src={user.idURL} alt="ID" sx={{ width: '100%', borderRadius: '12px', border: '1px solid rgba(16,117,186,0.1)' }} />
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(16,117,186,0.08)', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small"
          sx={{ borderRadius: '10px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a6a85', fontWeight: 600 }}>
          Close
        </Button>
        <Box sx={{ flex: 1 }} />
        {isUnapproved && (
          <Button onClick={() => onApprove(user)} variant="contained" size="small" disabled={approving}
            startIcon={approving ? <CircularProgress size={14} color="inherit" /> : <HowToReg />}
            sx={{ borderRadius: '10px', px: 2.5, background: 'linear-gradient(135deg, #0a9e74, #0d8060)', textTransform: 'none', fontWeight: 700 }}>
            {approving ? 'Approving…' : 'Approve User'}
          </Button>
        )}
        <Button onClick={() => onEdit(user)} variant="contained" size="small" startIcon={<Edit />}
          sx={{ borderRadius: '10px', background: '#91ce0f', textTransform: 'none', fontWeight: 700, boxShadow: '0 2px 8px rgba(16,117,186,0.3)', '&:hover': { background: '#0d64a3' } }}>
          Edit User
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Table skeleton loader ─────────────────────────────────────────────────────
function TableSkeleton({ cols = 7, rows = 8 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <TableCell key={j}><Skeleton variant="rounded" height={18} /></TableCell>
      ))}
    </TableRow>
  ));
}

// ── Reusable users table ──────────────────────────────────────────────────────
function UsersTable({ rows, loading, onRowClick, onApproveClick, approving, mode }) {
  const isUnapproved = mode === 'unapproved';
  return (
    <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {['#', 'User', 'Role', 'Project', 'Region', 'Mobile', 'Joined', isUnapproved ? 'Action' : 'Status'].map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', py: 1.5, background: '#f5f8fc' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton cols={8} rows={9} />
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Stack alignItems="center" spacing={1}>
                  <Person sx={{ fontSize: 44, color: '#c8d4dc' }} />
                  <Typography sx={{ color: '#6b7f8e', fontSize: '0.9rem' }}>
                    {isUnapproved ? 'No pending approvals' : 'No users found'}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : rows.map((user, idx) => {
            const color = avatarColor(fullName(user));
            return (
              <TableRow key={user._id} hover onClick={() => onRowClick(user)}
                sx={{ cursor: 'pointer', transition: 'background 0.12s', '&:hover': { background: alpha('#91ce0f', 0.04) } }}>
                <TableCell sx={{ color: '#b0bec5', fontSize: '0.75rem', width: 40 }}>{idx + 1}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Avatar src={user.photoURL} sx={{ width: 34, height: 34, fontSize: '0.78rem', fontWeight: 700, background: color, flexShrink: 0 }}>
                      {initials(user)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#0f2137', lineHeight: 1.2 }}>{fullName(user)}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#6b7f8e' }}>@{user.username}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  {user.role?.title
                    ? <Box sx={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 700, color: '#91ce0f', background: '#e6feff', border: '1px solid #7ec242', px: 0.9, py: 0.3, borderRadius: '6px', whiteSpace: 'nowrap' }}>{user.role.title}</Box>
                    : <Typography sx={{ color: '#c8d4dc', fontSize: '0.78rem' }}>—</Typography>}
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#0f2137' }}>{user.project?.title || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#0f2137' }}>{user.region?.title || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#4a6a85', fontFamily: 'monospace' }}>{user.mobileNumber || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6b7f8e' }}>{fmtDate(user.createdAt)}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  {isUnapproved ? (
                    <Button size="small" variant="contained"
                      onClick={(e) => { e.stopPropagation(); onApproveClick(user); }}
                      disabled={approving === user._id}
                      startIcon={approving === user._id ? <CircularProgress size={12} color="inherit" /> : <HowToReg sx={{ fontSize: '14px !important' }} />}
                      sx={{ fontSize: '0.7rem', py: 0.4, px: 1.25, borderRadius: '8px', background: 'linear-gradient(135deg, #0a9e74, #0d8060)', boxShadow: '0 2px 8px rgba(10,158,116,0.3)', whiteSpace: 'nowrap', '&:hover': { background: 'linear-gradient(135deg, #0d8060, #0a6e52)' } }}>
                      {approving === user._id ? 'Approving…' : 'Approve'}
                    </Button>
                  ) : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.68rem', fontWeight: 700, color: '#0a9e74', background: '#e6f9f4', border: '1px solid #b3ead9', px: 0.9, py: 0.3, borderRadius: '6px' }}>
                      <CheckCircle sx={{ fontSize: 11 }} /> Active
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UsersPage() {
  const token = useSelector(selectToken);

  const [tab,             setTab]             = useState(0);
  const [approvedRows,    setApprovedRows]    = useState([]);
  const [approvedTotal,   setApprovedTotal]   = useState(0);
  const [approvedPages,   setApprovedPages]   = useState(1);
  const [approvedPage,    setApprovedPage]    = useState(1);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [pendingRows,     setPendingRows]     = useState([]);
  const [pendingTotal,    setPendingTotal]    = useState(0);
  const [pendingPages,    setPendingPages]    = useState(1);
  const [pendingPage,     setPendingPage]     = useState(1);
  const [pendingLoading,  setPendingLoading]  = useState(false);
  const [search,          setSearch]          = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageSize,        setPageSize]        = useState(20);
  const [selectedUser,    setSelectedUser]    = useState(null);
  const [modalOpen,       setModalOpen]       = useState(false);
  const [approving,       setApproving]       = useState(null);
  const [snack,           setSnack]           = useState({ open: false, msg: '', severity: 'success' });
  const [formOpen,        setFormOpen]        = useState(false);
  const [editingUser,     setEditingUser]     = useState(null);

  const searchTimer = useRef(null);

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setApprovedPage(1); setPendingPage(1); }, 350);
  };

  const fetchBoth = useCallback(async () => {
    setApprovedLoading(true); setPendingLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    const ap = new URLSearchParams({ page: approvedPage, size: pageSize });
    const pp = new URLSearchParams({ page: pendingPage,  size: pageSize });
    if (debouncedSearch) { ap.set('search', debouncedSearch); pp.set('search', debouncedSearch); }

    const [ar, pr] = await Promise.allSettled([
      fetch(`${API_BASE}/get?${ap}`,           { headers }),
      fetch(`${API_BASE}/get/unapproved?${pp}`, { headers }),
    ]);

    const parse = (res, setRows, setTotal, setPages) => {
      if (res.status !== 'fulfilled') return;
      res.value.json().then((data) => {
        const rows = data.rows || data.data || (Array.isArray(data) ? data : []);
        setRows(rows);
        setTotal(data.total || rows.length);
        setPages(data.totalPages || data.lastPage || Math.ceil((data.total || rows.length) / pageSize) || 1);
      }).catch(console.error);
    };
    parse(ar, setApprovedRows, setApprovedTotal, setApprovedPages);
    parse(pr, setPendingRows,  setPendingTotal,  setPendingPages);
    setApprovedLoading(false); setPendingLoading(false);
  }, [token, approvedPage, pendingPage, pageSize, debouncedSearch]);

  const fetchApproved = useCallback(async () => {
    setApprovedLoading(true);
    try {
      const params = new URLSearchParams({ page: approvedPage, size: pageSize });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res  = await fetch(`${API_BASE}/get?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const rows = data.rows || data.data || (Array.isArray(data) ? data : []);
      setApprovedRows(rows); setApprovedTotal(data.total || rows.length);
      setApprovedPages(data.totalPages || data.lastPage || Math.ceil((data.total || rows.length) / pageSize) || 1);
    } catch (e) { console.error(e); }
    setApprovedLoading(false);
  }, [token, approvedPage, pageSize, debouncedSearch]);

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const params = new URLSearchParams({ page: pendingPage, size: pageSize });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res  = await fetch(`${API_BASE}/get/unapproved?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const rows = data.rows || data.data || (Array.isArray(data) ? data : []);
      setPendingRows(rows); setPendingTotal(data.total || rows.length);
      setPendingPages(data.totalPages || data.lastPage || Math.ceil((data.total || rows.length) / pageSize) || 1);
    } catch (e) { console.error(e); }
    setPendingLoading(false);
  }, [token, pendingPage, pageSize, debouncedSearch]);

  useEffect(() => { fetchBoth(); }, [fetchBoth]);

  const handleApprove = async (user) => {
    const userId = user._id || user;
    setApproving(userId);
    try {
      const res = await fetch(`${API_BASE}/approveUser/${userId}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      setSnack({ open: true, msg: 'User approved successfully!', severity: 'success' });
      if (selectedUser?._id === userId) setModalOpen(false);
      await Promise.all([fetchApproved(), fetchPending()]);
    } catch {
      setSnack({ open: true, msg: 'Failed to approve user. Please try again.', severity: 'error' });
    }
    setApproving(null);
  };

  const openUser    = (user) => { setSelectedUser(user); setModalOpen(true); };
  const handleEditUser = (user) => { setModalOpen(false); setEditingUser(user); setFormOpen(true); };
  const handleAddUser  = () => { setEditingUser(null); setFormOpen(true); };

  const handleSaved = (savedUser, wasEdit) => {
    setFormOpen(false);
    setSnack({ open: true, msg: wasEdit ? 'User updated!' : 'User created!', severity: 'success' });
    if (wasEdit) {
      setApprovedRows((prev) => prev.map((u) => u._id === savedUser._id ? { ...u, ...savedUser } : u));
      setPendingRows((prev) => prev.map((u) => u._id === savedUser._id ? { ...u, ...savedUser } : u));
    } else {
      fetchApproved();
    }
  };

  const isLoading = tab === 0 ? approvedLoading : pendingLoading;

  return (
        <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>


      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>Users</Typography>
          <Typography variant="body2" sx={{ color: '#4a6a85' }}>Manage field agents and approve new registrations</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', background: '#e6feff', border: '1px solid #7ec242' }}>
            <FiberManualRecord sx={{ fontSize: 8, color: '#91ce0f' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#91ce0f' }}>{approvedTotal.toLocaleString()} Active</Typography>
          </Box>
          {pendingTotal > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, borderRadius: '10px', background: '#fff4e0', border: '1px solid #ffd599' }}>
              <FiberManualRecord sx={{ fontSize: 8, color: '#c06000' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#c06000' }}>{pendingTotal} Pending</Typography>
            </Box>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={handleAddUser}
            sx={{ borderRadius: '10px', px: 2, fontWeight: 700, fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(16,117,186,0.3)', whiteSpace: 'nowrap' }}>
            Add User
          </Button>
        </Stack>
      </Box>

      {/* ── Controls ── */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <TextField size="small" placeholder="Search by username…" value={search} onChange={(e) => handleSearchChange(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ color: '#4a6a85', fontSize: 18 }} /></InputAdornment>,
            endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => handleSearchChange('')}><Close sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
          }} />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Per page</InputLabel>
          <Select value={pageSize} onChange={(e) => { setPageSize(e.target.value); setApprovedPage(1); setPendingPage(1); }} label="Per page">
            {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchBoth} disabled={approvedLoading || pendingLoading}
            sx={{ color: '#91ce0f', background: alpha('#91ce0f', 0.08), border: `1px solid ${alpha('#91ce0f', 0.2)}`, borderRadius: '10px', width: 36, height: 36 }}>
            {isLoading ? <CircularProgress size={16} sx={{ color: '#91ce0f' }} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Tabs ── */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ borderBottom: '1px solid rgba(16,117,186,0.1)', px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ '& .MuiTab-root': { fontSize: '0.82rem', fontWeight: 600, textTransform: 'none', minHeight: 48 }, '& .Mui-selected': { color: '#91ce0f' }, '& .MuiTabs-indicator': { background: '#91ce0f', height: 3, borderRadius: '3px 3px 0 0' } }}>
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CheckCircle sx={{ fontSize: 16 }} /><span>Active Users</span>
                <Box sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#91ce0f', background: '#e6feff', border: '1px solid #7ec242', px: 0.75, py: 0.15, borderRadius: '6px', lineHeight: '1.6' }}>
                  {approvedTotal.toLocaleString()}
                </Box>
              </Stack>
            } />
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <WarningAmber sx={{ fontSize: 16, color: tab === 1 ? '#c06000' : undefined }} /><span>Pending Approval</span>
                {pendingTotal > 0 && (
                  <Box sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#c06000', background: '#fff4e0', border: '1px solid #ffd599', px: 0.75, py: 0.15, borderRadius: '6px', lineHeight: '1.6' }}>
                    {pendingTotal}
                  </Box>
                )}
              </Stack>
            } />
          </Tabs>
        </Box>

        {tab === 0 && (
          <>
            <UsersTable rows={approvedRows} loading={approvedLoading} onRowClick={openUser} onApproveClick={handleApprove} approving={approving} mode="approved" />
            <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>Showing <strong>{approvedRows.length}</strong> of <strong>{approvedTotal.toLocaleString()}</strong> users</Typography>
              <Pagination count={approvedPages} page={approvedPage} onChange={(_, p) => setApprovedPage(p)} size="small" color="primary" sx={{ '& .MuiPaginationItem-root': { fontSize: '0.78rem' } }} />
            </Box>
          </>
        )}

        {tab === 1 && (
          <>
            {!pendingLoading && pendingRows.length > 0 && (
              <Box sx={{ mx: 2, mt: 1.5, px: 2, py: 1.25, borderRadius: '10px', background: '#fff4e0', border: '1px solid #ffd599', display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmber sx={{ fontSize: 16, color: '#c06000' }} />
                <Typography sx={{ fontSize: '0.8rem', color: '#c06000', fontWeight: 600 }}>
                  {pendingTotal} user{pendingTotal !== 1 ? 's' : ''} waiting for approval — click Approve on their row or open their profile to review and approve.
                </Typography>
              </Box>
            )}
            <UsersTable rows={pendingRows} loading={pendingLoading} onRowClick={openUser} onApproveClick={handleApprove} approving={approving} mode="unapproved" />
            <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(16,117,186,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>Showing <strong>{pendingRows.length}</strong> of <strong>{pendingTotal}</strong> pending</Typography>
              <Pagination count={pendingPages} page={pendingPage} onChange={(_, p) => setPendingPage(p)} size="small" color="primary" sx={{ '& .MuiPaginationItem-root': { fontSize: '0.78rem' } }} />
            </Box>
          </>
        )}
      </Card>

      {/* ── View modal ── */}
      <UserModal user={selectedUser} open={modalOpen} onClose={() => setModalOpen(false)}
        onApprove={handleApprove} approving={approving === selectedUser?._id} onEdit={handleEditUser} />

      {/* ── Add / Edit form dialog ── */}
      <UserFormDialog open={formOpen} onClose={() => setFormOpen(false)}
        initialUser={editingUser} onSaved={handleSaved} token={token} />

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: '12px', boxShadow: '0 8px 24px rgba(15,33,55,0.15)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
